// libraries
import React, { useState, useId } from 'react'

// components
import Breadcrumbs from '/react4xp/components/Breadcrumbs'
import Section from '/react4xp/components/Section'
import Form from '/react4xp/components/Form/Form'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Input from '/react4xp/components/Form/Input'
import Select from '/react4xp/components/Form/Select'
import TextArea from '/react4xp/components/Form/TextArea'
import Submit from '/react4xp/components/Form/Submit'
import Image from '/react4xp/components/Image'

// utils
import { isNonWhitespaceString, slugify } from '/lib/reactutils/utils'
import ReCaptcha from '../Recaptcha'
import { recaptchaCallbackFunction } from '/lib/reactutils/recaptcha'

export interface SupportContactFormProps {
	topInfos: {
		title: string
		intro: string
	}
	form: {
		endpoint: string
		onSuccess: () => void
		onError: () => void
	}
	steps: {
		step1: {
			title: string
			options: Array<{
				title: string
				options: Array<{
					label: string
				}>
			}>
			errorMessage: string
		}
		step2: {
			title: string
			inputs: Array<{
				type: 'input' | 'select' | 'textarea'
				input?: {
					type: 'text' | 'email' | 'tel'
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					errorMessages?: {
						required: string
						invalidEmail?: string
					}
				}
				select?: {
					id: string
					name: string
					label: string
					defaultValue: string
					options: Array<{
						label: string
						value: string
					}>
					required?: boolean
					errorMessages?: {
						required: string
					}
				}
				textarea?: {
					id: string
					name: string
					label: string
					required?: boolean
					helperText?: string
					placeholder?: string
					errorMessages?: {
						required: string
					}
				}
			}>
		}
	}
	privacyCheckbox: {
		name: string
		required: boolean
		errorMessages: {
			required: string
		}
		text: string
	}
	submit: {
		label: string
		successMessage: string
		validationMessage?: {
			one?: string
			many?: string
		}
	},
	siteKey?: string
	validationRecaptchaUrl?: string
}

export default function SupportContactForm({
	topInfos,
	form,
	steps,
	privacyCheckbox,
	submit,
	siteKey,
	validationRecaptchaUrl
}: SupportContactFormProps) {
	const instanceId = useId()

	const [verified, setVerified] = useState(false)

	return (
		<div className='page--forms'>

			<Section textColor='text-dark' className='top-section' bgColor='bg-beige-050'>
				<div className='grid-container'>
					<div className='grid-row'>
						<div className='c-12 c-md-10 c-lg-8'>

							{isNonWhitespaceString(topInfos?.title) && (
								<h1 className='title-lg top-section__title'>
									{topInfos?.title}
								</h1>
							)}

							{isNonWhitespaceString(topInfos?.intro) && (
								<p
									dangerouslySetInnerHTML={{ __html: topInfos?.intro }}
									suppressHydrationWarning
									className='top-section__intro'
								/>
							)}

						</div>
					</div>
				</div>
			</Section>

			<Section textColor='text-dark' className='form-section' bgColor='bg-beige-050'>
				<div className='grid-container'>
					<Form
						endpoint={form?.endpoint}
						clearOnSuccess
						onSuccess={form?.onSuccess}
						onError={form?.onError}
					>
						<Input type="text" hidden value="support_contact_form" name="form_type" id="form_type" />

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>1.</span> {steps?.step1?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--grid-4'>
							<div className='content-wrapper'>
								<div className='grid-row'>

									{steps?.step1?.options.map((item, i) => {
										const groupLabelId = `${instanceId}-checkbox-group-${i}`

										return item.options?.length ? <div className='c-12 c-sm-6 c-lg-3' key={i}>

											{isNonWhitespaceString(item.title) && (
												<p id={groupLabelId} className='labels-xl-medium subtitle'>
													{item.title}
												</p>
											)}

											<div
												className='checkboxes'
												role={isNonWhitespaceString(item.title) ? 'group' : undefined}
												aria-labelledby={isNonWhitespaceString(item.title) ? groupLabelId : undefined}
											>
												{item.options?.map((option, i2) => (
													<Checkbox
														key={i2}
														name={`${item.title}__${option.label}`}
													>
														{option.label}
													</Checkbox>
												))}
											</div>

										</div> : <></>
									})}
								</div>
							</div>
						</div>

						<div className='form-section__title'>
							<p className='labels-xxl-medium'>
								<span className='step-number'>2.</span> {steps?.step2?.title}
							</p>
						</div>

						<div className='form-section__content form-section__content--space-left'>
							<div className='grid-row'>

								<div className='c-md-1 c-lg-2'></div>
								<div className='c-md-1 border'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>
									{steps?.step2?.inputs.map((item, i) => {
										switch (item.type) {
											case 'input':
												return (
													<Input
														key={i}
														type={item.input.type}
														id={item.input.id}
														name={item.input.name}
														label={item.input.label}
														helperText={item.input.helperText}
														required={item.input.required}
														errorMessages={item.input.errorMessages}
													/>
												)
											case 'select':
												return (
													<Select
														key={i}
														id={item.select.id}
														name={item.select.name}
														label={item.select.label}
														defaultValue={item.select.defaultValue}
														options={item.select.options}
														required={item.select.required}
														errorMessages={item.select.errorMessages}
													/>
												)
											case 'textarea':
												return (
													<TextArea
														key={i}
														id={item.textarea.id}
														name={item.textarea.name}
														label={item.textarea.label}
														placeholder={item.textarea.placeholder}
														required={item.textarea.required}
														errorMessages={item.textarea.errorMessages}
													/>
												)
											default:
												return null
										}
									})}
								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

						<div className='form-section__last-block'>
							<div className='grid-row'>

								<div className='c-md-2 c-lg-3'></div>

								<div className='c-md-9 c-lg-8 content-wrapper'>

									{siteKey ? <div className='recaptcha'>
										<ReCaptcha 
											siteKey={siteKey} 
											callback={(token) => recaptchaCallbackFunction(token, setVerified, validationRecaptchaUrl)}
											setVerified={setVerified}
										/>
									</div> : <></>}

									{privacyCheckbox && (
										<Checkbox
											name={privacyCheckbox?.name}
											required={privacyCheckbox?.required}
											errorMessages={privacyCheckbox?.errorMessages}
										>
											{privacyCheckbox?.text}
										</Checkbox>
									)}

									<Submit
										disabled={siteKey && !verified}
										label={submit?.label}
										successMessage={submit?.successMessage}
										validationMessage={submit?.validationMessage}
									/>

								</div>

								<div className='c-md-1'></div>

							</div>
						</div>

					</Form>
				</div>
			</Section>

		</div>
	)
}